import { getTeamRatings } from '../../api/client';
import { MedalIcon, CheckIcon, HourglassIcon, TrophyIcon } from '../shared/Icons';
import EmptyState from '../shared/EmptyState';
import ErrorState from '../shared/ErrorState';
import { useI18n } from '../../i18n';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import { useLoad } from '../../hooks/useLoad';
import { boutScoreState } from '../../utils/tournamentState';
import type { Bout } from '../../types';

interface TeamRating {
  team_id: string;
  team_name: string;
  logo_url?: string;
  gold: number;
  silver: number;
  bronze: number;
  total_points: number;
}

interface TeamRatingsTabProps {
  tournamentId: string;
  onPrint: () => void;
  bouts: Bout[];
}

export default function TeamRatingsTab({ tournamentId, onPrint, bouts }: TeamRatingsTabProps) {
  const { t } = useI18n();
  // #552 — a failed load is not "no ratings": it shows the error state.
  const load = useLoad(
    () => getTeamRatings(tournamentId).then(r => r.data as unknown as TeamRating[]),
    [tournamentId],
    { context: 'TeamRatingsTab:ratings', initial: [] },
  );
  const { data: ratings, loading } = load;
  // #458 — the loading text shows after 200 ms and holds 400 ms (useDeferredLoading):
  // a fast load shows nothing, not a one-frame flash.
  const showLoading = useDeferredLoading(loading);

  if (showLoading) return <p className="text-gray-400 text-sm">{t.common.loading}</p>;
  if (loading) return null;
  if (load.error) return <ErrorState compact onRetry={load.reload} />;
  // #552 — say why the list is empty: the ratings count the bracket medals.
  if (!ratings.length) return (
    <EmptyState compact icon={<TrophyIcon className="w-10 h-10" />}
      title={t.tournament.ratingsEmptyTitle} hint={t.tournament.ratingsEmptyHint} />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 no-print">
        {(() => {
          const { scored, total, all: allDone } = boutScoreState(bouts);
          return allDone
            ? <span className="badge badge-green text-xs inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {total}/{total} {t.tournament.ratingsFinal}</span>
            : <span className="badge badge-amber text-xs inline-flex items-center gap-1"><HourglassIcon className="w-3 h-3" /> {scored}/{total} {t.tournament.ratingsPartial}</span>;
        })()}
        <div className="flex-1" />
        <button onClick={onPrint} className="btn-outline btn-sm">{t.common.print}</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-left text-[10px] text-gray-500 uppercase tracking-wide">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">{t.common.team}</th>
              <th className="px-3 py-2 text-center"><MedalIcon className="w-5 h-5 inline-block" place={1} /></th>
              <th className="px-3 py-2 text-center"><MedalIcon className="w-5 h-5 inline-block" place={2} /></th>
              <th className="px-3 py-2 text-center"><MedalIcon className="w-5 h-5 inline-block" place={3} /></th>
              <th className="px-3 py-2 text-center font-bold">{t.common.points}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ratings.map((r, i) => (
              <tr key={r.team_id} className={i === 0 ? 'bg-amber-50' : i === 1 ? 'bg-gray-100' : i === 2 ? 'bg-orange-50' : ''}>
                <td className="px-3 py-2 font-bold text-gray-400">{i === 0 ? <MedalIcon className="w-5 h-5 inline-block" place={1} /> : i === 1 ? <MedalIcon className="w-5 h-5 inline-block" place={2} /> : i === 2 ? <MedalIcon className="w-5 h-5 inline-block" place={3} /> : i + 1}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {r.logo_url ? <img src={r.logo_url} alt="" className="w-5 h-5 rounded-sm object-cover" /> : null}
                    <span className="font-semibold">{r.team_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-center font-bold text-amber-600">{r.gold || '—'}</td>
                <td className="px-3 py-2 text-center font-bold text-gray-500">{r.silver || '—'}</td>
                <td className="px-3 py-2 text-center font-bold text-orange-700">{r.bronze || '—'}</td>
                <td className={`px-3 py-2 text-center text-sm ${i < 3 ? 'font-black' : 'font-normal text-gray-500'}`}>{r.total_points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
